import { useState, useEffect } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { CalendarPlus, Search, UserPlus, Paperclip, X, Loader2, FileText, CalendarClock } from "lucide-react";

const inp = "w-full px-3 py-2.5 rounded-xl border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#1E5AA8]/30 focus:border-[#1E5AA8]";

const fmtTime12 = (t) => {
  if (!t || !t.includes(":")) return t || "";
  const [H, M] = t.split(":").map((x) => parseInt(x, 10));
  if (Number.isNaN(H)) return t;
  const ap = H >= 12 ? "PM" : "AM";
  let h = H % 12; if (h === 0) h = 12;
  return `${h}:${String(M || 0).padStart(2, "0")} ${ap}`;
};

export default function BookAppointment() {
  const [mode, setMode] = useState("existing");
  const [searchQ, setSearchQ] = useState("");
  const [searchRes, setSearchRes] = useState([]);
  const [patient, setPatient] = useState(null);
  const [np, setNp] = useState({ name: "", age: "", gender: "Male", phone: "", whatsapp: "", email: "" });
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [time, setTime] = useState("");
  const [notes, setNotes] = useState("");
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (searchQ.length < 2) { setSearchRes([]); return; }
    const t = setTimeout(async () => {
      const r = await api.get("/patients", { params: { q: searchQ } });
      setSearchRes(r.data);
    }, 250);
    return () => clearTimeout(t);
  }, [searchQ]);

  const onFiles = (e) => {
    const list = Array.from(e.target.files || []).slice(0, 8);
    setFiles((prev) => [...prev, ...list].slice(0, 8));
    e.target.value = "";
  };

  const book = async () => {
    if (!date || !time) { toast.error("Choose appointment date & time"); return; }
    if (mode === "existing" && !patient) { toast.error("Select a patient"); return; }
    if (mode === "new" && (!np.name || !np.age)) { toast.error("Enter patient name & age"); return; }
    setBusy(true);
    try {
      const payload = mode === "existing"
        ? { patient_id: patient.patient_id, date, time, notes }
        : { name: np.name, age: parseInt(np.age), gender: np.gender, phone: np.phone, whatsapp: np.whatsapp, email: np.email, date, time, notes };
      const r = await api.post("/appointments", payload);
      const appt = r.data;
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        await api.post(`/appointments/${appt.id}/reports`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success(`Appointment booked for ${appt.patient.name} · ${date} ${fmtTime12(time)}`);
      setPatient(null); setSearchQ(""); setNp({ name: "", age: "", gender: "Male", phone: "", whatsapp: "", email: "" });
      setTime(""); setNotes(""); setFiles([]);
    } catch (e) {
      toast.error("Booking failed: " + (e.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-5" data-testid="book-appointment-tab">
      <div className="rounded-2xl bg-gradient-to-r from-[#EBF3FB] to-[#FFFBEB] border border-blue-100 p-5 flex items-center gap-3">
        <div className="w-11 h-11 rounded-2xl bg-[#1E5AA8] flex items-center justify-center"><CalendarPlus className="w-6 h-6 text-amber-300" /></div>
        <div>
          <h2 className="font-display text-2xl font-bold text-slate-900">Book an Appointment</h2>
          <p className="text-sm text-slate-500">Schedule a visit and attach the patient's previous digital reports.</p>
        </div>
      </div>

      <div className="rounded-2xl bg-white border border-slate-100 shadow-sm p-5 space-y-4">
        <div className="flex gap-2">
          <ModePill active={mode === "existing"} onClick={() => setMode("existing")} icon={Search} label="Existing Patient" testId="book-mode-existing" />
          <ModePill active={mode === "new"} onClick={() => setMode("new")} icon={UserPlus} label="New Patient" testId="book-mode-new" />
        </div>

        {mode === "existing" ? (
          <div>
            <label className="text-xs font-medium text-slate-500 mb-1 block">Find patient by ID / name / phone</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input data-testid="book-patient-search" value={searchQ} onChange={(e) => setSearchQ(e.target.value)} placeholder="Search patient…" className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E5AA8]/30" />
            </div>
            {patient ? (
              <div className="mt-3 flex items-center justify-between rounded-xl bg-[#EBF3FB] border border-blue-100 p-3">
                <div><span className="font-medium text-slate-800">{patient.name}</span> <span className="text-xs text-slate-400">{patient.age}/{patient.gender}</span> <span className="text-xs font-mono-id text-[#1E5AA8]">{patient.patient_id}</span></div>
                <button onClick={() => setPatient(null)} className="text-slate-400 hover:text-red-500"><X className="w-4 h-4" /></button>
              </div>
            ) : (
              <div className="mt-2 space-y-1 max-h-48 overflow-auto">
                {searchRes.map((p) => (
                  <button key={p.patient_id} onClick={() => setPatient(p)} className="w-full text-left p-2.5 rounded-xl border border-slate-100 hover:bg-[#EBF3FB] text-sm">
                    <span className="font-medium">{p.name}</span> <span className="text-xs text-slate-400">{p.age}/{p.gender}</span> <span className="text-xs font-mono-id text-[#1E5AA8] float-right">{p.patient_id}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-3 sm:col-span-1"><label className="text-xs text-slate-500">Full Name *</label><input data-testid="book-new-name" value={np.name} onChange={(e) => setNp({ ...np, name: e.target.value })} className={inp} /></div>
            <div><label className="text-xs text-slate-500">Age *</label><input data-testid="book-new-age" type="number" value={np.age} onChange={(e) => setNp({ ...np, age: e.target.value })} className={inp} /></div>
            <div><label className="text-xs text-slate-500">Gender</label><select data-testid="book-new-gender" value={np.gender} onChange={(e) => setNp({ ...np, gender: e.target.value })} className={inp}>{["Male", "Female", "Other"].map((g) => <option key={g}>{g}</option>)}</select></div>
            <div className="col-span-3 sm:col-span-1"><label className="text-xs text-slate-500">Phone</label><input data-testid="book-new-phone" value={np.phone} onChange={(e) => setNp({ ...np, phone: e.target.value })} className={inp} placeholder="+91 98765 43210" /></div>
            <div className="col-span-3 sm:col-span-1"><label className="text-xs text-slate-500">WhatsApp (if different)</label><input data-testid="book-new-whatsapp" value={np.whatsapp} onChange={(e) => setNp({ ...np, whatsapp: e.target.value })} className={inp} placeholder="With country code" /></div>
            <div className="col-span-3 sm:col-span-1"><label className="text-xs text-slate-500">Email</label><input data-testid="book-new-email" type="email" value={np.email} onChange={(e) => setNp({ ...np, email: e.target.value })} className={inp} placeholder="Patient email" /></div>
            <p className="col-span-3 text-[11px] text-slate-400">A unique Patient ID is generated automatically. Phone / WhatsApp / email are used to share the prescription with the patient.</p>
          </div>
        )}

        <div className="grid sm:grid-cols-2 gap-3">
          <div><label className="text-xs text-slate-500">Appointment Date *</label><input data-testid="book-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className={inp} /></div>
          <div>
            <label className="text-xs text-slate-500">Appointment Time *</label>
            <div data-testid="book-time"><TimePicker12 value={time} onChange={setTime} /></div>
          </div>
        </div>

        <div>
          <label className="text-xs text-slate-500">Notes (optional)</label>
          <input data-testid="book-notes" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Reason for visit…" className={inp} />
        </div>

        <div>
          <label className="text-xs text-slate-500 mb-1 block">Previous Digital Reports (PDF / JPG)</label>
          <label className="flex items-center justify-center gap-2 rounded-xl border-2 border-dashed border-slate-200 hover:border-[#1E5AA8] py-5 cursor-pointer transition-colors text-slate-500 hover:text-[#1E5AA8]">
            <Paperclip className="w-4 h-4" /> <span className="text-sm font-medium">Attach reports</span>
            <input data-testid="book-reports-upload" type="file" accept=".pdf,image/jpeg,image/png,image/jpg" multiple className="hidden" onChange={onFiles} />
          </label>
          {files.length > 0 && (
            <div className="mt-2 space-y-1">
              {files.map((f, i) => (
                <div key={i} className="flex items-center justify-between text-sm bg-slate-50 rounded-lg px-3 py-2">
                  <span className="inline-flex items-center gap-2 text-slate-700 truncate"><FileText className="w-4 h-4 text-[#1E5AA8]" />{f.name}</span>
                  <button onClick={() => setFiles(files.filter((_, x) => x !== i))} className="text-slate-300 hover:text-red-500"><X className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          )}
        </div>

        <button data-testid="book-submit" onClick={book} disabled={busy} className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-xl bg-[#1E5AA8] text-white font-semibold hover:bg-[#123C73] disabled:opacity-60">
          {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <CalendarClock className="w-5 h-5" />} Book Appointment
        </button>
      </div>
    </div>
  );
}

function ModePill({ active, onClick, icon: Icon, label, testId }) {
  return (
    <button data-testid={testId} onClick={onClick} className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-colors ${active ? "bg-[#1E5AA8] text-white shadow-sm" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
      <Icon className="w-4 h-4" /> {label}
    </button>
  );
}

// 12-hour (1–12 + AM/PM) picker that emits a 24h "HH:MM" string.
function TimePicker12({ value, onChange }) {
  const parse = (v) => {
    if (!v || !v.includes(":")) return { h: "", m: "", ap: "AM" };
    const [H, M] = v.split(":").map((x) => parseInt(x, 10));
    const ap = H >= 12 ? "PM" : "AM";
    let h = H % 12; if (h === 0) h = 12;
    return { h: String(h), m: String(M).padStart(2, "0"), ap };
  };
  const { h, m, ap } = parse(value);
  const emit = (nh, nm, nap) => {
    if (!nh) { onChange(""); return; }
    let H = parseInt(nh, 10) % 12;
    if (nap === "PM") H += 12;
    onChange(`${String(H).padStart(2, "0")}:${nm || "00"}`);
  };
  const hours = Array.from({ length: 12 }, (_, i) => String(i + 1));
  const mins = ["00", "05", "10", "15", "20", "25", "30", "35", "40", "45", "50", "55"];
  return (
    <div className="grid grid-cols-3 gap-2 mt-0.5">
      <select data-testid="book-time-hour" value={h} onChange={(e) => emit(e.target.value, m || "00", ap)} className={inp}>
        <option value="">Hr</option>
        {hours.map((x) => <option key={x} value={x}>{x}</option>)}
      </select>
      <select data-testid="book-time-minute" value={m} onChange={(e) => emit(h || "9", e.target.value, ap)} className={inp}>
        {mins.map((x) => <option key={x} value={x}>{x}</option>)}
      </select>
      <select data-testid="book-time-ampm" value={ap} onChange={(e) => emit(h || "9", m || "00", e.target.value)} className={inp}>
        <option value="AM">AM</option>
        <option value="PM">PM</option>
      </select>
    </div>
  );
}
